import CheckIcon from "@mui/icons-material/Check";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PartnerJoinApi from "../../../infrastructure/api/partnerJoin";
import SolarentSiteFooter from "../../components/marketing/SolarentSiteFooter";
import PanelInfo from "../../components/shared/panel-info";

const TEAL_DEEP = "#044F79";
const TEAL_MID = "#0A7EA4";
const SOLAR_GOLD = "#F6AE2D";

const MOBILE_PATTERN = /^09\d{9}$/;

const BENEFITS = [
  {
    icon: LinkOutlinedIcon,
    title: "لینک اختصاصی معرفی",
    body: "برای هر نماینده لینک اختصاصی جهت معرفی مشتریان ایجاد می‌شود.",
  },
  {
    icon: PeopleOutlineIcon,
    title: "پنل مدیریت مشتریان",
    body: "ثبت‌نام‌ها و پروژه‌های معرفی‌شده را مشاهده و پیگیری کنید.",
  },
  {
    icon: TrackChangesOutlinedIcon,
    title: "مشاهده وضعیت پروژه‌ها",
    body: "مراحل پیشرفت پروژه‌های معرفی‌شده در پنل قابل مشاهده است.",
  },
  {
    icon: AccountBalanceWalletOutlinedIcon,
    title: "مدیریت پورسانت و تسویه",
    body: "پورسانت‌ها و درخواست‌های تسویه را به‌صورت آنلاین مدیریت کنید.",
  },
  {
    icon: HubOutlinedIcon,
    title: "امکان توسعه شبکه همکاری",
    body: "در صورت فعال بودن سطح دسترسی، امکان ایجاد و مدیریت زیرمجموعه وجود خواهد داشت.",
  },
];

const PROCESS_STEPS = [
  {
    title: "ثبت درخواست همکاری",
    description: "اطلاعات اولیه خود را ثبت می‌کنید.",
  },
  {
    title: "بررسی و تایید",
    description: "درخواست توسط تیم سولارنت بررسی می‌شود.",
  },
  {
    title: "دریافت پنل و لینک اختصاصی",
    description: "پس از تایید، حساب نمایندگی و لینک معرفی شما فعال می‌شود.",
  },
];

const POST_SUBMIT_ITEMS = [
  "درخواست شما بررسی می‌شود",
  "در صورت تایید، حساب نمایندگی فعال خواهد شد",
  "لینک اختصاصی معرفی دریافت می‌کنید",
  "امکان مشاهده مشتریان معرفی‌شده و پورسانت‌ها فراهم می‌شود",
];

const HERO_CHIPS = [
  "لینک اختصاصی معرفی",
  "مشاهده مشتریان معرفی شده",
  "مشاهده وضعیت پروژه‌ها",
  "مدیریت پورسانت و تسویه",
];

const SECTION_SX = {
  mt: "56px",
  mb: "32px",
};

const SECTION_TITLE_SX = {
  mb: 3,
  fontSize: { xs: "1.05rem", md: "1.15rem" },
};

const CARD_SX = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: alpha(TEAL_DEEP, 0.1),
  boxShadow: `0 8px 24px ${alpha(TEAL_DEEP, 0.06)}`,
  bgcolor: "#fff",
};

const fa2en = (value) =>
  String(value || "")
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

const normalizeMobile = (value) => {
  const digits = fa2en(value).replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("9")) return `0${digits}`;
  if (digits.length === 11 && digits.startsWith("09")) return digits;
  return digits;
};

const validateNationalCode = (code) => {
  if (!/^\d{10}$/.test(code)) return false;
  if (/^(\d)\1{9}$/.test(code)) return false;
  const check = Number(code[9]);
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(code[i]) * (10 - i);
  const remainder = sum % 11;
  return (remainder < 2 && check === remainder) || (remainder >= 2 && check === 11 - remainder);
};

const BenefitCard = ({ icon: Icon, title, body }) => (
  <Card
    elevation={0}
    sx={{
      ...CARD_SX,
      height: "auto",
      minHeight: "140px",
    }}
  >
    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: alpha(SOLAR_GOLD, 0.18),
            color: TEAL_DEEP,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} color={TEAL_DEEP} sx={{ mb: 0.25, lineHeight: 1.35 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.5} sx={{ fontSize: "0.8125rem" }}>
            {body}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const PostSubmitCheckItem = ({ text }) => (
  <Stack direction="row" spacing={0.75} alignItems="flex-start">
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        bgcolor: alpha(SOLAR_GOLD, 0.22),
        color: TEAL_DEEP,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: 0.15,
      }}
    >
      <CheckIcon sx={{ fontSize: 12, fontWeight: 700 }} />
    </Box>
    <Typography variant="body2" color="text.secondary" lineHeight={1.5} sx={{ fontSize: "0.8125rem" }}>
      {text}
    </Typography>
  </Stack>
);

const ProcessStepCard = ({ step, title, description, isLast }) => (
  <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
    <Card elevation={0} sx={{ ...CARD_SX, height: "100%" }}>
      <CardContent sx={{ p: 2 }}>
        <Chip
          label={`مرحله ${step}`}
          size="small"
          sx={{
            mb: 1,
            fontWeight: 700,
            bgcolor: alpha(TEAL_MID, 0.12),
            color: TEAL_DEEP,
            border: `1px solid ${alpha(TEAL_MID, 0.2)}`,
          }}
        />
        <Typography variant="subtitle2" fontWeight={800} color={TEAL_DEEP} sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
          {description}
        </Typography>
      </CardContent>
    </Card>
    {!isLast && (
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          top: "50%",
          left: -12,
          width: 24,
          height: 2,
          bgcolor: alpha(SOLAR_GOLD, 0.7),
          transform: "translateY(-50%)",
          zIndex: 0,
        }}
      />
    )}
  </Box>
);

const PartnerJoinLandingPage = () => {
  const { referralCode } = useParams();
  const [loadingPage, setLoadingPage] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pageInfo, setPageInfo] = useState(null);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    nationalCode: "",
    mobileNumber: "",
  });
  const [errors, setErrors] = useState({});

  const parentDisplayName = useMemo(
    () => pageInfo?.parentDisplayName || pageInfo?.ParentDisplayName || "",
    [pageInfo]
  );

  const inviterCode = useMemo(
    () => pageInfo?.referralCode || pageInfo?.ReferralCode || referralCode || "",
    [pageInfo, referralCode]
  );

  const showInviterBadge = Boolean(pageInfo?.canRegister && (parentDisplayName || inviterCode));

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingPage(true);
        const res = await PartnerJoinApi.getJoinPage(referralCode);
        if (res?.isSuccess) {
          setPageInfo(res.data);
        } else {
          setPageInfo({
            canRegister: false,
            message: res?.message || "کد معرف نامعتبر است.",
          });
        }
      } catch {
        setPageInfo({
          canRegister: false,
          message: "خطا در بارگذاری اطلاعات. لطفاً دوباره تلاش کنید.",
        });
      } finally {
        setLoadingPage(false);
      }
    };
    load();
  }, [referralCode]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName?.trim()) next.firstName = "نام الزامی است.";
    if (!form.lastName?.trim()) next.lastName = "نام خانوادگی الزامی است.";

    const nationalCode = fa2en(form.nationalCode).replace(/\D/g, "");
    if (!nationalCode) next.nationalCode = "کد ملی الزامی است.";
    else if (!validateNationalCode(nationalCode)) next.nationalCode = "کد ملی معتبر نیست.";

    const mobile = normalizeMobile(form.mobileNumber);
    if (!mobile) next.mobileNumber = "شماره موبایل الزامی است.";
    else if (!MOBILE_PATTERN.test(mobile)) {
      next.mobileNumber = "شماره موبایل باید ۱۱ رقم و با 09 شروع شود.";
    }

    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSubmitting(true);
      const res = await PartnerJoinApi.submitJoin({
        referralCode,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        nationalCode: fa2en(form.nationalCode).replace(/\D/g, ""),
        mobileNumber: normalizeMobile(form.mobileNumber),
      });
      if (res?.isSuccess) {
        setDone(true);
        toast.success(res.message || "درخواست شما ثبت شد.");
      } else {
        toast.error(res?.message || "ثبت درخواست ناموفق بود.");
      }
    } catch {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#F6F8FB",
        background: `linear-gradient(180deg, ${alpha(TEAL_DEEP, 0.06)} 0%, #F6F8FB 28%, #F6F8FB 100%)`,
      }}
    >
      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 2 } }}>
        {/* Top bar logo */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 2px 8px ${alpha(TEAL_DEEP, 0.08)}`,
              border: "1px solid",
              borderColor: alpha(TEAL_DEEP, 0.08),
            }}
          >
            <PanelInfo size={28} />
          </Box>
          <Button component={Link} to="/auth/login" variant="text" size="small" sx={{ color: TEAL_DEEP }}>
            ورود به حساب
          </Button>
        </Stack>

        {/* Hero */}
        <Card
          elevation={0}
          sx={{
            ...CARD_SX,
            mb: 2.5,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${TEAL_DEEP} 0%, ${TEAL_MID} 100%)`,
            border: "none",
            color: "#fff",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack spacing={2} alignItems={{ xs: "stretch", sm: "flex-start" }}>
              <Typography
                component="h1"
                variant="h4"
                fontWeight={800}
                sx={{ fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2rem" }, lineHeight: 1.45 }}
              >
                به شبکه نمایندگان سولارنت بپیوندید
              </Typography>
              <Typography
                variant="body1"
                sx={{ opacity: 0.94, lineHeight: 1.85, maxWidth: 720, fontSize: { xs: "0.9rem", sm: "1rem" } }}
              >
                از طریق لینک اختصاصی خود مشتری معرفی کنید، وضعیت پروژه‌ها را پیگیری کنید و از
                همکاری‌های موفق پورسانت دریافت نمایید.
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {HERO_CHIPS.map((label) => (
                  <Chip
                    key={label}
                    icon={<CheckIcon sx={{ fontSize: "16px !important", color: `${SOLAR_GOLD} !important` }} />}
                    label={label}
                    size="small"
                    sx={{
                      bgcolor: alpha("#fff", 0.14),
                      color: "#fff",
                      border: `1px solid ${alpha("#fff", 0.22)}`,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      height: 28,
                      "& .MuiChip-icon": { mr: -0.25 },
                    }}
                  />
                ))}
              </Stack>
              {showInviterBadge && (
                <Box
                  sx={{
                    display: "inline-flex",
                    flexDirection: "column",
                    alignSelf: { xs: "stretch", sm: "flex-start" },
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha("#fff", 0.12),
                    border: `1px solid ${alpha(SOLAR_GOLD, 0.45)}`,
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.85, mb: 0.25 }}>
                    معرفی شده توسط
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: SOLAR_GOLD }}>
                    {parentDisplayName || `کد نماینده: ${inviterCode}`}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {loadingPage ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: TEAL_DEEP }} />
          </Box>
        ) : done ? (
          <Card elevation={0} sx={{ ...CARD_SX, mb: 2 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                درخواست شما ثبت شد. پس از تایید مدیر سامانه، حساب و لینک اختصاصی شما فعال می‌شود و از
                طریق پیامک مطلع خواهید شد.
              </Alert>
              <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                تیم سولارنت درخواست شما را بررسی می‌کند. پس از تایید، لینک اختصاصی معرفی و دسترسی
                به پنل نمایندگی برای شما فعال خواهد شد.
              </Typography>
            </CardContent>
          </Card>
        ) : !pageInfo?.canRegister ? (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            {pageInfo?.message || "امکان ثبت‌نام وجود ندارد."}
          </Alert>
        ) : (
          <>
            {/* Benefits — 3+2 balanced layout */}
            <Box sx={SECTION_SX}>
              <Typography variant="h6" fontWeight={800} color={TEAL_DEEP} sx={SECTION_TITLE_SX}>
                مزایای همکاری با سولارنت
              </Typography>
              <Grid container spacing={1.5}>
                {BENEFITS.slice(0, 3).map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.title}>
                    <BenefitCard {...item} />
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={1.5} sx={{ mt: 1.5, justifyContent: { md: "center" } }}>
                {BENEFITS.slice(3).map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.title}>
                    <BenefitCard {...item} />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Process */}
            <Box sx={SECTION_SX}>
              <Typography variant="h6" fontWeight={800} color={TEAL_DEEP} sx={SECTION_TITLE_SX}>
                مسیر شروع همکاری
              </Typography>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 1.5, md: 2 }}
                sx={{ position: "relative" }}
              >
                {PROCESS_STEPS.map((step, index) => (
                  <ProcessStepCard
                    key={step.title}
                    step={index + 1}
                    title={step.title}
                    description={step.description}
                    isLast={index === PROCESS_STEPS.length - 1}
                  />
                ))}
              </Stack>
            </Box>

            {/* Form */}
            <Box sx={SECTION_SX}>
              <Divider sx={{ mb: 3, borderColor: alpha(TEAL_DEEP, 0.12) }} />
              <Typography variant="h6" fontWeight={800} color={TEAL_DEEP} sx={{ mb: 1 }}>
                ثبت درخواست همکاری
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.75 }}>
                اطلاعات اولیه خود را وارد کنید تا درخواست همکاری شما بررسی شود.
              </Typography>

              <Card elevation={0} sx={{ ...CARD_SX }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box component="form" onSubmit={onSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="نام"
                        value={form.firstName}
                        onChange={(e) => handleChange("firstName", e.target.value)}
                        fullWidth
                        required
                        error={Boolean(errors.firstName)}
                        helperText={errors.firstName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="نام خانوادگی"
                        value={form.lastName}
                        onChange={(e) => handleChange("lastName", e.target.value)}
                        fullWidth
                        required
                        error={Boolean(errors.lastName)}
                        helperText={errors.lastName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="کد ملی"
                        value={form.nationalCode}
                        onChange={(e) => handleChange("nationalCode", e.target.value)}
                        fullWidth
                        required
                        inputProps={{ maxLength: 10 }}
                        error={Boolean(errors.nationalCode)}
                        helperText={errors.nationalCode}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="شماره موبایل"
                        value={form.mobileNumber}
                        onChange={(e) => handleChange("mobileNumber", e.target.value)}
                        fullWidth
                        required
                        placeholder="09123456789"
                        error={Boolean(errors.mobileNumber)}
                        helperText={errors.mobileNumber}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        اطلاعات شما تنها جهت بررسی درخواست همکاری استفاده می‌شود.
                      </Typography>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={submitting}
                        startIcon={
                          submitting ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <GroupAddIcon />
                          )
                        }
                        sx={{
                          py: 1.35,
                          fontWeight: 800,
                          bgcolor: TEAL_DEEP,
                          "&:hover": { bgcolor: alpha(TEAL_DEEP, 0.88) },
                          boxShadow: `0 8px 20px ${alpha(TEAL_DEEP, 0.25)}`,
                        }}
                      >
                        {submitting ? "در حال ثبت..." : "ثبت درخواست همکاری"}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {/* Post-submit info */}
                <Box
                  sx={{
                    mt: 1.75,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(TEAL_MID, 0.06),
                    border: `1px solid ${alpha(TEAL_MID, 0.14)}`,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800} color={TEAL_DEEP} sx={{ mb: 1 }}>
                    پس از ثبت درخواست چه اتفاقی می‌افتد؟
                  </Typography>
                  <Grid container columnSpacing={2} rowSpacing={0.75}>
                    {POST_SUBMIT_ITEMS.map((item) => (
                      <Grid item xs={12} sm={6} key={item}>
                        <PostSubmitCheckItem text={item} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </Card>
            </Box>
          </>
        )}
      </Container>

      <SolarentSiteFooter />
    </Box>
  );
};

export default PartnerJoinLandingPage;
